import { BasePage } from '@/components/ui/base-page'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { FileText, Newspaper, BookOpen, GraduationCap, MessageSquare, Megaphone } from 'lucide-react'

type Filter = 'all' | 'blog' | 'shared'

export function InsightsFeedPage() {
  const [filter, setFilter] = useState<Filter>('all')

  return (
    <BasePage
      title="Insights"
      description="Industry knowledge, research, and updates — filter by category to discover content."
    >
      <div className="space-y-8">
        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 border-b pb-4">
          <button
            type="button"
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted hover:bg-muted/80'
            }`}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setFilter('blog')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'blog'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted hover:bg-muted/80'
            }`}
          >
            Blog
          </button>
          <button
            type="button"
            onClick={() => setFilter('shared')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'shared'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted hover:bg-muted/80'
            }`}
          >
            Shared Insights
          </button>
        </div>

        {/* Quick links for discovery */}
        <div className="grid md:grid-cols-2 gap-6">
          {(filter === 'all' || filter === 'blog') && (
            <>
              <Link
                to="/insights/blog/opinion"
                className="flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <MessageSquare className="h-8 w-8 text-primary shrink-0" />
                <div>
                  <h3 className="font-semibold">Opinion</h3>
                  <p className="text-sm text-muted-foreground">Perspectives and commentary from our team.</p>
                </div>
              </Link>
              <Link
                to="/insights/blog/partner"
                className="flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <MessageSquare className="h-8 w-8 text-primary shrink-0" />
                <div>
                  <h3 className="font-semibold">Partner Insights</h3>
                  <p className="text-sm text-muted-foreground">Insights from our partners and ecosystem.</p>
                </div>
              </Link>
              <Link
                to="/insights/blog/announcements"
                className="flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <Megaphone className="h-8 w-8 text-primary shrink-0" />
                <div>
                  <h3 className="font-semibold">Investment Announcements</h3>
                  <p className="text-sm text-muted-foreground">Latest announcements and updates.</p>
                </div>
              </Link>
              <Link
                to="/insights/blog/all"
                className="flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <FileText className="h-8 w-8 text-primary shrink-0" />
                <div>
                  <h3 className="font-semibold">All Media Content</h3>
                  <p className="text-sm text-muted-foreground">Browse all blog and media content.</p>
                </div>
              </Link>
            </>
          )}
          {(filter === 'all' || filter === 'shared') && (
            <>
              <Link
                to="/insights/shared/reports"
                className="flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <FileText className="h-8 w-8 text-primary shrink-0" />
                <div>
                  <h3 className="font-semibold">Industry Reports</h3>
                  <p className="text-sm text-muted-foreground">Reports and analysis by industry.</p>
                </div>
              </Link>
              <Link
                to="/insights/shared/learning"
                className="flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <GraduationCap className="h-8 w-8 text-primary shrink-0" />
                <div>
                  <h3 className="font-semibold">Learning Resources</h3>
                  <p className="text-sm text-muted-foreground">Learning nuggets and educational content.</p>
                </div>
              </Link>
              <Link
                to="/insights/shared/news"
                className="flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <Newspaper className="h-8 w-8 text-primary shrink-0" />
                <div>
                  <h3 className="font-semibold">Financial News</h3>
                  <p className="text-sm text-muted-foreground">Curated financial news and updates.</p>
                </div>
              </Link>
              <Link
                to="/insights/shared/research"
                className="flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <BookOpen className="h-8 w-8 text-primary shrink-0" />
                <div>
                  <h3 className="font-semibold">Research Papers</h3>
                  <p className="text-sm text-muted-foreground">Research and new knowledge from our team.</p>
                </div>
              </Link>
            </>
          )}
        </div>
      </div>
    </BasePage>
  )
}
